import { type WheelEventHandler } from './wheel-event-handler.ts';

const GESTURE_TIMEOUT_MS = 150;

/**
 * Coordinates wheel event handling across multiple gesture handlers.
 *
 * Uses a "handler lock-in" mechanism: once a handler is selected for a gesture,
 * all subsequent wheel events are routed exclusively to that handler until the
 * gesture completes (either via explicit gesture-end signal or timeout). This
 * prevents gesture confusion (e.g. switching from scroll to pan mid-gesture).
 */
export class WheelGestureCoordinator {
  private lockedHandler: WheelEventHandler | null = null;
  private lastEventTime = 0;

  /**
   * Creates a new wheel gesture coordinator.
   * @param handlers - The handlers to use to coordinate the wheel events.
   */
  constructor(private handlers: WheelEventHandler[]) {}

  public handleWheel(event: WheelEvent): void {
    const now = Date.now();

    // Check if this is the very first wheel event in a new wheel gesture
    if (this.shouldSelectNewHandler(event, now)) {
      // New gesture detected. Find a handler and "lock" entire gesture against it
      this.selectAndLockHandler(event);

      // Start the handler for the new gesture
      this.lockedHandler?.start(event);
    }

    this.lockedHandler?.handleEvent(event);
    this.lastEventTime = now;
  }

  public unlockHandler(): void {
    this.lockedHandler?.cleanup();
    this.lockedHandler = null;
    this.lastEventTime = 0;
  }

  public cleanup(): void {
    this.unlockHandler();
  }

  /**
   * Determines if a new handler should be selected for a given wheel event.
   * Handler lock-in is broken (allowing selection of a new handler) when:
   * (a) there is no current handler, or
   * (b) the last event was more than GESTURE_TIMEOUT_MS ago (gesture completed via timeout), or
   * (c) the event is a zoom event and the current handler cannot handle it (zoom can interrupt other gestures)
   *
   * Note: If the current handler can already handle a zoom event, we keep using it to avoid
   * unnecessary start/cleanup cycles for subsequent zoom events in the same gesture.
   */
  private shouldSelectNewHandler(event: WheelEvent, now: number): boolean {
    const isZoomEvent = event.ctrlKey || event.metaKey;
    if (isZoomEvent && this.lockedHandler?.canHandle(event)) {
      return false; // Current handler can handle zoom, keep using it
    }

    const isNewGesture =
      !this.lockedHandler || now - this.lastEventTime > GESTURE_TIMEOUT_MS;

    return isZoomEvent || isNewGesture;
  }

  /**
   * Selects a new handler for a given wheel event and locks the gesture against it.
   *
   * Why do we "lock"? The coordinator enforces a rule that every wheel gesture must
   * only map to a single user gesture which is predetermined at the start. As an example,
   * we do NOT want for the canvas to pan after a container finishes scrolling because
   * of the weird parallax effect that may confused the user.
   */
  private selectAndLockHandler(event: WheelEvent): void {
    this.lockedHandler?.cleanup();

    const handler = this.handlers.find((h) => h.canHandle(event));
    if (!handler) {
      this.lockedHandler = null;
      return;
    }

    this.lockedHandler = handler;
  }
}
